// =============================================================================
// PettyCashPage — combined list + create
// =============================================================================
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiGet, apiPost, getApiError } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Money } from '../../../components/Money';
import { StatusPill } from '../../../components/StatusPill';
import { EmailDocButton } from '../../../components/EmailDocButton';
import { PageScaffold } from '../../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../../components/DataTable';
import { cn } from '../../../lib/cn';

type Kind = 'FLOAT_TOPUP' | 'EXPENSE';

interface VoucherRow {
  id: string;
  number: string;
  kind: Kind;
  amount: string;
  description: string;
  voucherDate: string;
  expenseAccount: { code: string; name: string } | null;
  createdBy: { name: string };
  approvedBy: { name: string } | null;
}

interface BankAccountOption {
  id: string;
  name: string;
  kind: string;
  account: { code: string; name: string };
}

export function PettyCashPage(): JSX.Element {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const list = useQuery({
    queryKey: ['petty-cash'],
    queryFn: () => apiGet<VoucherRow[]>('/petty-cash'),
    refetchInterval: 60_000,
  });

  const HEADER_ACTIONS = (
    <Button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : '+ New voucher'}</Button>
  );

  return (
    <PageScaffold
      title="Petty Cash"
      subtitle="Float top-ups + small operational expenses paid from petty cash."
      actions={HEADER_ACTIONS}
      scroll={false}
    >
      {showForm && (
        <div className="shrink-0">
          <NewVoucherForm
            onDone={() => {
              setShowForm(false);
              qc.invalidateQueries({ queryKey: ['petty-cash'] });
            }}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      <DataTableCard>
        {list.isLoading ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : (list.data ?? []).length === 0 ? (
          <div className="text-center text-ink-3 px-6 py-12 text-sm italic">No vouchers yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className={STICKY_HEAD}>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                <th className="px-6 py-2 font-medium">PCV</th>
                <th className="px-6 py-2 font-medium">Date</th>
                <th className="px-6 py-2 font-medium">Kind</th>
                <th className="px-6 py-2 font-medium">Description</th>
                <th className="px-6 py-2 font-medium">Account</th>
                <th className="px-6 py-2 font-medium text-right">Amount</th>
                <th className="px-6 py-2 font-medium">Approval</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((v) => (
                <tr key={v.id} className="border-b border-rule-2 hover:bg-paper-2">
                  <td className="px-6 py-3 kc-mono text-xs text-ink">{v.number}</td>
                  <td className="px-6 py-3 text-xs text-ink-3">
                    {new Date(v.voucherDate).toLocaleDateString('en-MU', {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </td>
                  <td className="px-6 py-3">
                    <StatusPill tone={v.kind === 'FLOAT_TOPUP' ? 'info' : 'warning'}>
                      {v.kind === 'FLOAT_TOPUP' ? 'top-up' : 'expense'}
                    </StatusPill>
                  </td>
                  <td className="px-6 py-3 text-ink max-w-xs truncate">{v.description}</td>
                  <td className="px-6 py-3 text-xs text-ink-3">
                    {v.expenseAccount ? `${v.expenseAccount.code} ${v.expenseAccount.name}` : '—'}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <Money value={v.amount} />
                  </td>
                  <td className="px-6 py-3 text-xs">
                    {v.approvedBy ? (
                      <StatusPill tone="positive">approved</StatusPill>
                    ) : (
                      <StatusPill tone="warning">pending</StatusPill>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right space-x-2">
                    <Button
                      variant="tertiary"
                      size="xs"
                      onClick={() => downloadPdf(v.id, v.number)}
                    >
                      PDF
                    </Button>
                    <EmailDocButton
                      endpoint={`/petty-cash/${v.id}/email`}
                      selfDefault
                      title={`Email voucher ${v.number}`}
                      subtitle="Send the petty cash voucher PDF by email."
                      renderTrigger={(open) => (
                        <Button variant="tertiary" size="xs" onClick={open}>
                          Email
                        </Button>
                      )}
                    />
                    {!v.approvedBy && <ApproveButton voucherId={v.id} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTableCard>
    </PageScaffold>
  );
}

async function downloadPdf(id: string, number: string): Promise<void> {
  try {
    const res = await api.get(`/petty-cash/${id}/pdf`, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${number}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  } catch (e) {
    console.error(e);
  }
}

function ApproveButton({ voucherId }: { voucherId: string }): JSX.Element {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => apiPost(`/petty-cash/${voucherId}/approve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['petty-cash'] }),
  });
  return (
    <Button variant="tertiary" size="xs" onClick={() => m.mutate()} disabled={m.isPending}>
      {m.isPending ? 'Approving…' : 'Approve'}
    </Button>
  );
}

// ─── New voucher form ───────────────────────────────────────────────────────
interface AccountOption {
  id: string;
  code: string;
  name: string;
  type: string;
}

function NewVoucherForm({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const { user } = useAuth();
  const [kind, setKind] = useState<Kind>('EXPENSE');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [expenseAccountCode, setExpenseCode] = useState('6300'); // utilities default
  const [sourceBankAccountId, setSource] = useState('');
  const [receiptUrl, setReceiptUrl] = useState('');
  const [err, setErr] = useState<string | null>(null);

  // Expense accounts via the same accounts feed used by JV (trial balance can
  // be reused, but we'd rather have an /accounts list). Stub: hardcoded common.
  const expenseAccounts: AccountOption[] = [
    { id: '6100', code: '6100', name: 'Salaries', type: 'EXPENSE' },
    { id: '6200', code: '6200', name: 'Rent', type: 'EXPENSE' },
    { id: '6300', code: '6300', name: 'Utilities', type: 'EXPENSE' },
    { id: '6400', code: '6400', name: 'Commissions', type: 'EXPENSE' },
    { id: '6500', code: '6500', name: 'Delivery Expense', type: 'EXPENSE' },
    { id: '6920', code: '6920', name: 'Stock Adjustments', type: 'EXPENSE' },
  ];

  // Bank accounts for the float top-up dropdown
  const banks = useQuery({
    queryKey: ['bank-accounts-list'],
    queryFn: () =>
      apiGet<BankAccountOption[]>('/bank-deposits/_bank-accounts').catch(
        () => [] as BankAccountOption[],
      ),
  });
  // Float top-up sources: real cash positions only. Banks (withdraw cash) +
  // cash drawers (pull from till takings). Excludes petty_cash, in_transit,
  // and clearing accounts (card/cheque/mobile) which aren't immediately spendable.
  const bankSources = useMemo(
    () => (banks.data ?? []).filter((b) => b.kind === 'bank'),
    [banks.data],
  );
  const drawerSources = useMemo(
    () => (banks.data ?? []).filter((b) => b.kind === 'cash_drawer'),
    [banks.data],
  );

  const submit = useMutation({
    mutationFn: () =>
      apiPost(
        '/petty-cash',
        kind === 'EXPENSE'
          ? {
              kind: 'EXPENSE',
              amount: Number(amount),
              description,
              expenseAccountCode,
              receiptUrl: receiptUrl || undefined,
            }
          : {
              kind: 'FLOAT_TOPUP',
              amount: Number(amount),
              description,
              sourceBankAccountId,
              receiptUrl: receiptUrl || undefined,
            },
      ),
    onSuccess: onDone,
    onError: (e) => setErr(getApiError(e).message),
  });

  const canSubmit =
    !!description.trim() &&
    Number(amount) > 0 &&
    !submit.isPending &&
    (kind === 'EXPENSE' ? !!expenseAccountCode : !!sourceBankAccountId);

  void user;
  return (
    <Card
      title="New petty cash voucher"
      subtitle="Posts to the GL immediately. Approval by another user is required for the audit trail."
    >
      <div className="space-y-4">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setKind('EXPENSE')}
            className={cn(
              'px-3 py-1.5 rounded text-xs uppercase tracking-wide border',
              kind === 'EXPENSE'
                ? 'bg-accent text-white border-accent'
                : 'bg-paper-2 text-ink-2 border-rule',
            )}
          >
            Expense spend
          </button>
          <button
            type="button"
            onClick={() => setKind('FLOAT_TOPUP')}
            className={cn(
              'px-3 py-1.5 rounded text-xs uppercase tracking-wide border',
              kind === 'FLOAT_TOPUP'
                ? 'bg-accent text-white border-accent'
                : 'bg-paper-2 text-ink-2 border-rule',
            )}
          >
            Float top-up
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            label="Amount (MUR)"
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          {kind === 'EXPENSE' ? (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
                Expense account
              </span>
              <select
                value={expenseAccountCode}
                onChange={(e) => setExpenseCode(e.target.value)}
                className="kc-input"
              >
                {expenseAccounts.map((a) => (
                  <option key={a.code} value={a.code}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
                Top up from
              </span>
              <select
                value={sourceBankAccountId}
                onChange={(e) => setSource(e.target.value)}
                className="kc-input"
              >
                <option value="">— pick source —</option>
                {bankSources.length > 0 && (
                  <optgroup label="Bank accounts">
                    {bankSources.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} ({b.account.code})
                      </option>
                    ))}
                  </optgroup>
                )}
                {drawerSources.length > 0 && (
                  <optgroup label="Cash drawers (from sales takings)">
                    {drawerSources.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name} ({b.account.code})
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </label>
          )}
        </div>

        <Input
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={
            kind === 'EXPENSE' ? 'e.g. Water + electricity at HQ' : 'e.g. Replenish HQ float'
          }
        />
        <Input
          label="Receipt URL (optional)"
          value={receiptUrl}
          onChange={(e) => setReceiptUrl(e.target.value)}
          placeholder="https://… (S3 link to receipt photo)"
        />

        <div className="flex items-center justify-between pt-2 border-t border-rule">
          {err && <div className="text-xs text-negative">{err}</div>}
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setErr(null);
                submit.mutate();
              }}
              disabled={!canSubmit}
            >
              {submit.isPending ? 'Posting…' : 'Save voucher'}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
